// AI-assisted Google review helper. Submission mode is Bridal / Group B only;
// standalone mode serves the public QR review page without submission data.
//
// Decoupled generation / tap flow (Feb-2026 surgical fix for iOS Safari):
//
//   1. Customer selects aspect chips (and optionally writes a note).
//   2. A short coalescing delay triggers a fast, shared library lookup. No
//      customer request calls OpenAI; replenishment happens server-side.
//   3. Once the draft lands in state, the single primary CTA
//      ("Continue to Google Review") becomes enabled.
//   4. Tapping the CTA runs ONE direct call to `navigator.clipboard
//      .writeText(draft)` and nothing else before it — the gesture is
//      preserved on iOS because there is no prior async API work in the
//      click handler. On success, `window.location.assign` navigates in
//      the same tab. On clipboard failure, the component swaps to a
//      fallback view with the draft text visible and a single OPEN
//      GOOGLE REVIEW link — the customer is NEVER sent to Google with
//      an empty clipboard.
//
// Submission mode retains its existing endpoint, group / rating gates, rate
// limit, Google URL, 7-chip pre-delivery aspect list, and OpenAI behavior.

import React, { useEffect, useRef, useState } from "react";
import { ExternalLink, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { TANISH_GOOGLE_REVIEW_URL } from "@/lib/googleReview";

// Pre-delivery aspects ONLY. Codes MUST match the backend
// ALLOWED_REVIEW_ASPECTS set.
const SUBMISSION_ASPECT_CHIPS = [
  { code: "design_selection",             label: "Design & Selection" },
  { code: "variety_collection",           label: "Variety / Collection" },
  { code: "customisation_options",        label: "Customisation Options" },
  { code: "staff_assistance",             label: "Staff Assistance" },
  { code: "shopping_experience",          label: "Shopping Experience" },
  { code: "communication_responsiveness", label: "Communication / Responsiveness" },
  { code: "store_experience",             label: "Store Experience" },
];

const STANDALONE_ASPECT_CHIPS = SUBMISSION_ASPECT_CHIPS;

// Briefly coalesce rapid chip taps before a MongoDB library reservation.
// This keeps the draft display near-instant without calling OpenAI live.
const GEN_DEBOUNCE_MS = 80;

// Synchronous legacy clipboard fallback for environments without the
// modern Clipboard API. Safe to call inside a user gesture.
function legacyCopySync(text) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand && document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch {
    return false;
  }
}

export default function ReviewAssistant({ submissionId, mode = "submission" }) {
  const isStandalone = mode === "standalone";
  const aspectChips = isStandalone ? STANDALONE_ASPECT_CHIPS : SUBMISSION_ASPECT_CHIPS;
  const [aspects, setAspects] = useState([]);
  const [note, setNote] = useState("");
  const [draft, setDraft] = useState("");
  // Stages:
  //   idle       — no aspects chosen yet
  //   preparing  — exact library lookup or approved fallback composition in flight
  //   ready      — draft available, final CTA active
  //   opening    — final CTA tapped, navigation starting
  //   error      — draft lookup failed, retry label shown on CTA
  //   recovery   — no approved draft was available; customer can write one manually
  //   cliperror  — clipboard write failed, fallback UI shown instead
  const [stage, setStage] = useState("idle");
  const [error, setError] = useState("");
  const [noteApplied, setNoteApplied] = useState(false);

  // Memoise exact aspect combinations so re-renders / React StrictMode
  // double-invocations never create duplicate reservations. Notes are sent
  // with the settled request but never create another reservation by itself.
  const lastGenKeyRef = useRef("");
  const inFlightKeyRef = useRef("");
  // Belt-and-braces lock for the final tap, kept out of state so a very
  // fast double-tap cannot slip through the gap before React disables
  // the button on re-render.
  const tapLockRef = useRef(false);

  function keyFor(aspectsList, noteStr) {
    return JSON.stringify({
      aspects: [...aspectsList].sort(),
    });
  }

  async function generate(key, aspectsList, noteStr) {
    inFlightKeyRef.current = key;
    setStage("preparing");
    setError("");
    try {
      const endpoint = isStandalone
        ? "/review/standalone"
        : `/submissions/${submissionId}/review-draft`;
      const r = await api.post(endpoint, {
        aspects: aspectsList,
        note: noteStr.trim() || null,
      });
      const text = (r?.data?.draft || "").trim();
      if (!text) throw new Error("empty");
      // Only commit if the user hasn't changed their selections in the
      // meantime — the newer selection will have its own in-flight key.
      if (inFlightKeyRef.current === key) {
        setDraft(text);
        setNoteApplied(Boolean(r?.data?.note_applied));
        lastGenKeyRef.current = key;
        setStage("ready");
      }
    } catch (requestError) {
      if (inFlightKeyRef.current === key) {
        setDraft("");
        setNoteApplied(false);
        if (requestError?.response?.status === 503) {
          setStage("recovery");
          setError("Write your own review below, then continue to Google Reviews.");
        } else {
          setStage("error");
          setError("We couldn't prepare your review right now. Please tap below to try again.");
        }
      }
    } finally {
      if (inFlightKeyRef.current === key) {
        inFlightKeyRef.current = "";
      }
    }
  }

  // Auto-trigger a library reservation after aspect selection settles.
  // An optional note is sent only as a safe factual boundary; it never starts
  // a live generation request or causes an additional reservation by itself.
  useEffect(() => {
    // Never retrigger generation while navigation is in-flight.
    if (stage === "opening") return undefined;

    // No selection → clear everything and stay idle.
    if (aspects.length === 0) {
      lastGenKeyRef.current = "";
      inFlightKeyRef.current = "";
      setDraft("");
      setNoteApplied(false);
      setStage((s) => (s === "preparing" || s === "ready" || s === "error" ? "idle" : s));
      setError("");
      return undefined;
    }

    const key = keyFor(aspects, note);
    // Already generated for this exact combination — nothing to do.
    if (key === lastGenKeyRef.current) return undefined;
    // Already fetching for this exact combination — nothing to do.
    if (key === inFlightKeyRef.current) return undefined;

    const timer = setTimeout(() => {
      generate(key, aspects, note);
    }, GEN_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aspects, submissionId, isStandalone]);

  function toggle(code) {
    if (stage === "preparing" || stage === "opening" || tapLockRef.current) return;
    setAspects((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  }

  function retry() {
    // Allow re-generation for the same key.
    lastGenKeyRef.current = "";
    inFlightKeyRef.current = "";
    generate(keyFor(aspects, note), aspects, note);
  }

  /**
   * Shared final CTA — iOS-safe single tap for submission and standalone modes.
   * The ONLY async op before clipboard write is… the clipboard write
   * itself. There is no prior `await` on an API call, so the user
   * gesture context is preserved for Safari's permission check.
   */
  async function continueToGoogleReview() {
    if ((stage !== "ready" && stage !== "recovery") || !draft || tapLockRef.current) return;
    tapLockRef.current = true;

    let copied = false;
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(draft);
        copied = true;
      } catch {
        /* fall through to legacy sync fallback */
      }
    }
    if (!copied) {
      copied = legacyCopySync(draft);
    }

    if (copied) {
      setStage("opening");
      try {
        // Same-tab navigation — immune to popup blockers, works on iOS.
        window.location.assign(TANISH_GOOGLE_REVIEW_URL);
      } catch {
        // Extremely unlikely for same-origin assign; show fallback.
        setStage("cliperror");
        tapLockRef.current = false;
      }
      // Keep tapLockRef locked while navigation begins.
    } else {
      // Clipboard write failed — stay on the page, surface fallback.
      setStage("cliperror");
      tapLockRef.current = false;
    }
  }

  // Clipboard-failure fallback — only after a real clipboard failure.
  if (stage === "cliperror") {
    return (
      <div data-testid="review-assistant" className="mt-6 max-w-md mx-auto">
        <p className="overline text-[10px] text-center mb-2">Your review</p>
        <div
          data-testid="review-fallback-text"
          className="w-full text-sm text-ink bg-white border border-line rounded-2xl px-4 py-3
            leading-relaxed whitespace-pre-wrap select-text"
        >
          {draft}
        </div>
        <p
          data-testid="review-fallback-msg"
          className="mt-3 text-xs text-muted2 text-center leading-relaxed"
        >
          We couldn&rsquo;t copy the review automatically. Please tap and hold the review text to
          copy it.
        </p>
        <a
          data-testid="feedback-google-btn"
          href={TANISH_GOOGLE_REVIEW_URL}
          rel="noreferrer noopener"
          className="btn-maroon inline-flex items-center justify-center gap-2 mt-4 w-full
            !text-[11px] tracking-[0.28em] uppercase"
        >
          <ExternalLink size={13} strokeWidth={1.6} />
          Open Google Review
        </a>
      </div>
    );
  }

  // Button label / disabled state driven by stage.
  const chipsLocked = stage === "preparing" || stage === "opening";
  const canTap = (stage === "ready" || stage === "recovery") && !!draft;
  const buttonDisabled =
    stage === "preparing" || stage === "opening" ||
    (stage !== "error" && !canTap);
  let buttonLabel = "Rate us on Google";
  if (stage === "preparing") buttonLabel = "Preparing your review…";
  else if (stage === "opening") buttonLabel = "Opening Google…";
  else if (stage === "error") buttonLabel = "Try again";

  return (
    <div data-testid="review-assistant" className="mt-6">
      <p className="overline text-[10px] text-center mb-3">
        {isStandalone
          ? "What would you like to highlight?"
          : "What stood out so far?"}
      </p>
      <div className="flex flex-wrap gap-2 justify-center">
        {aspectChips.map(({ code, label }) => {
          const active = aspects.includes(code);
          return (
            <button
              key={code}
              type="button"
              data-testid={`review-aspect-${code}`}
              onClick={() => toggle(code)}
              aria-pressed={active}
              disabled={chipsLocked}
              className={[
                "text-[11px] tracking-[0.14em] uppercase px-3 py-2 rounded-full border",
                "transition-colors",
                active
                  ? "bg-maroon text-white border-maroon"
                  : "bg-white text-ink border-line hover:border-maroon/40",
                chipsLocked ? "opacity-60 cursor-not-allowed" : "",
              ].join(" ")}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="mt-5 max-w-md mx-auto">
        <label
          htmlFor="review-note-input"
          className="overline text-[10px] block mb-2 text-center"
        >
          {isStandalone
            ? "Anything else you’d like to include?"
            : "Anything else you’d like to mention?"}{" "}
          <span className="text-muted2 lowercase tracking-normal">(optional)</span>
        </label>
        <textarea
          id="review-note-input"
          data-testid="review-note-input"
          value={note}
          onChange={(e) => {
            setNote(e.target.value.slice(0, 500));
            if (stage === "ready") {
              setNoteApplied(false);
            }
          }}
          disabled={chipsLocked}
          rows={2}
          maxLength={500}
          placeholder={
            isStandalone
              ? "Add an accurate detail about your selected highlights (optional)"
              : "Tell us in your own words (optional)"
          }
          className="w-full text-sm text-ink placeholder:text-muted2/70 bg-ivory/60 border
            border-line rounded-2xl px-4 py-3 leading-relaxed focus:outline-none focus:ring-2
            focus:ring-maroon/25 focus:border-maroon/40 resize-none disabled:opacity-60"
        />
      </div>

      {note.trim() && stage === "ready" && (
        <p className="mt-3 text-xs text-muted2 text-center" data-testid="review-note-status">
          {noteApplied
            ? "Your note was included in the draft."
            : "Your review is editable—add any accurate detail from your note if you wish."}
        </p>
      )}

      {/* Every customer can refine their truthful review before the shared Google action. */}
      {(draft || stage === "recovery") &&
        (stage === "ready" || stage === "opening" || stage === "recovery") && (
        <div className="mt-5 max-w-md mx-auto">
          <p className="overline text-[10px] mb-2 text-center">
            {isStandalone ? "Suggested review" : "Your review"}
          </p>
          <textarea
            data-testid={stage === "recovery" ? "review-recovery-editor" : "review-draft-editor"}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value.slice(0, 800));
              setError("");
            }}
            rows={5}
            maxLength={800}
            placeholder={stage === "recovery" ? "Write your review here" : undefined}
            className="w-full text-sm text-ink bg-white border border-line rounded-2xl px-4
              py-3 leading-relaxed resize-y focus:outline-none focus:ring-2
              focus:ring-maroon/25 focus:border-maroon/40"
          />
        </div>
      )}

      <div className="mt-5 max-w-md mx-auto">
        <button
          type="button"
          data-testid="review-one-tap-btn"
          onClick={stage === "error" ? retry : continueToGoogleReview}
          disabled={buttonDisabled}
          aria-busy={stage === "preparing" || stage === "opening"}
          className="btn-maroon inline-flex items-center justify-center gap-2 w-full
            !text-[11px] tracking-[0.24em] uppercase disabled:opacity-50
            disabled:cursor-not-allowed"
        >
          <Sparkles size={13} strokeWidth={1.6} />
          {buttonLabel}
        </button>
      </div>

      {error && (
        <p
          data-testid="review-error"
          className="mt-4 text-xs text-[#8A2A2A] text-center"
        >
          {error}
        </p>
      )}
    </div>
  );
}
